import { Activity, Component } from '@cms-max/medicare-api-sdk'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { medicareApiClient } from 'clients/medicareApiClient'
import Message from 'models/Message'
import { RootState } from 'redux/config'
import { Logger } from 'utils/logger'

export interface ToastReducer {
  // Toasts are actually just message center messages
  list: Message[],
  status?: 'finished' | 'error' | 'loading'
}

export const initialState: ToastReducer = {
  list: []
}

export const fetchToasts = createAsyncThunk(
  '/toasts/fetchToasts',
  async (language: 'en' | 'es') => {
    try {
      const toasts = await medicareApiClient.asyncGetToasts(language)
      return toasts
    } catch (e: unknown) {
      // Log and then throw an error so that the reducer fetchToasts.rejected is still called
      Logger.error('Failed to load toasts', e)
      throw new Error('Failed to load toasts')
    }
  }
)

// This is a fun name, please do not change :)
export const sliceOfToast = createSlice({
  name: 'toasts',
  initialState,
  reducers: {
    updateToastAsRead: (state, action: PayloadAction<string>) => {
      const toast = state.list.find(toast => toast.guid === action.payload)
      if (toast) {
        toast.read = true
        const markMessageRead = async () => {
          try {
            // Toasts are actually messages, so we just use the mark messages as read endpoint
            await medicareApiClient.asyncMarkMessagesAsRead({ messageIds: [action.payload] })
          } catch {
            // We don't actually care right now if marking the toast read succeeds.
            // If it fails, it will just open again when they go to a new tab or window
            Logger.error('Failed to mark toast as read.')
          }
        }
        markMessageRead()
        window.globalHeaderEvents?.handleEvent({
          core: {
            activity: Activity.MessageRead,
            property: 'MAX',
            component: Component.MessageCenter,
            coreClickData: {
              profileId: toast.profileId,
              messageId: toast.guid
            }
          }
        })
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchToasts.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchToasts.fulfilled, (state, action: { payload: Message[] }) => {
        state.list = action.payload
        state.status = 'finished'
      })
      .addCase(fetchToasts.rejected, (state) => {
        state.status = 'error'
      })
  }
})

export const {
  updateToastAsRead
} = sliceOfToast.actions

export const getToasts = (state: RootState) => state.toasts

export default sliceOfToast.reducer
